import type { Timeframe } from '@shared/types/candle'
import { TIMEFRAME_LABELS } from '@shared/types/candle'
import {
  AreaChart,
  BarChart2,
  Bookmark,
  Camera,
  Check,
  Copy,
  Download,
  FlaskConical,
  LineChart,
  Save,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { LiveTick } from '@/hooks/useLiveTick'
import { useChartStore } from '@/stores/chartStore'
import type { ChartTemplate } from '@/stores/templateStore'
import type { ChartType } from '@/types/chart'
import { cn } from '@/utils/cn'
import { formatPrice } from '@/utils/format'

const CHART_TYPES: { type: ChartType; icon: React.ReactNode; label: string }[] = [
  { type: 'candlestick', icon: <BarChart2 className="h-3.5 w-3.5" />, label: 'Candlestick' },
  { type: 'line', icon: <LineChart className="h-3.5 w-3.5" />, label: 'Line' },
  { type: 'area', icon: <AreaChart className="h-3.5 w-3.5" />, label: 'Area' },
  { type: 'baseline', icon: <TrendingUp className="h-3.5 w-3.5" />, label: 'Baseline' },
]

const VISIBLE_TFS: Timeframe[] = ['1m', '5m', '15m', '1h', '4h', '1D', '1W', '1M']

interface ChartToolbarProps {
  panelId: string
  isActive: boolean
  onActivate: () => void
  liveTick?: LiveTick | null
  onIndicatorClick?: () => void
  activeIndicatorCount?: number
  templates?: ChartTemplate[]
  onSaveTemplate?: (name: string) => void
  onApplyTemplate?: (templateId: string) => void
  onDeleteTemplate?: (templateId: string) => void
  onDownloadImage?: () => void
  onCopyImage?: () => void
  onCustomIndicatorClick?: () => void
  customIndicatorCount?: number
}

// ── Template dropdown ─────────────────────────────────────────────────────────

function TemplateMenu({
  templates,
  onSave,
  onApply,
  onDelete,
  onClose,
}: {
  templates: ChartTemplate[]
  onSave: (name: string) => void
  onApply: (id: string) => void
  onDelete: (id: string) => void
  onClose: () => void
}) {
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onClose])

  function submitSave() {
    const n = name.trim()
    if (n) onSave(n)
    setSaving(false)
    setName('')
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-full z-50 min-w-48 rounded border border-tv-border bg-tv-bg-secondary shadow-xl"
    >
      <div className="border-b border-tv-border p-2">
        {saving ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSave()
                if (e.key === 'Escape') {
                  setSaving(false)
                  setName('')
                }
              }}
              placeholder="Template name…"
              className="flex-1 rounded border border-tv-border bg-tv-bg-primary px-1.5 py-0.5 text-2xs text-tv-text-primary outline-none focus:border-tv-blue"
            />
            <button
              type="button"
              onClick={submitSave}
              className="rounded p-0.5 text-tv-blue hover:bg-tv-bg-tertiary"
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => {
                setSaving(false)
                setName('')
              }}
              className="rounded p-0.5 text-tv-text-muted hover:bg-tv-bg-tertiary"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setSaving(true)}
            className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-2xs text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
          >
            <Save className="h-3 w-3" />
            Save as template
          </button>
        )}
      </div>

      {templates.length === 0 ? (
        <div className="px-3 py-2 text-2xs text-tv-text-muted">No saved templates</div>
      ) : (
        <div className="max-h-48 overflow-y-auto py-1">
          {templates.map((t) => (
            <div
              key={t.id}
              className="group flex items-center gap-1 px-2 py-0.5 hover:bg-tv-bg-tertiary"
            >
              <button
                type="button"
                onClick={() => {
                  onApply(t.id)
                  onClose()
                }}
                className="flex-1 truncate py-0.5 text-left text-2xs text-tv-text-primary"
              >
                {t.name}
              </button>
              <button
                type="button"
                onClick={() => onDelete(t.id)}
                title="Delete template"
                className="hidden rounded p-0.5 text-tv-text-muted hover:text-tv-red group-hover:flex"
              >
                <Trash2 className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Camera dropdown ───────────────────────────────────────────────────────────

function CameraMenu({
  onDownload,
  onCopy,
  onClose,
}: {
  onDownload: () => void
  onCopy: () => void
  onClose: () => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onClose])

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-full z-50 min-w-40 rounded border border-tv-border bg-tv-bg-secondary shadow-xl"
    >
      <div className="py-1">
        <button
          type="button"
          onClick={() => {
            onDownload()
            onClose()
          }}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-2xs text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
        >
          <Download className="h-3 w-3" />
          Download PNG
        </button>
        <button
          type="button"
          onClick={() => {
            onCopy()
            onClose()
          }}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-2xs text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
        >
          <Copy className="h-3 w-3" />
          Copy to clipboard
        </button>
      </div>
    </div>
  )
}

// ── Main toolbar ──────────────────────────────────────────────────────────────

export function ChartToolbar({
  panelId,
  isActive,
  onActivate,
  liveTick,
  onIndicatorClick,
  activeIndicatorCount = 0,
  templates = [],
  onSaveTemplate,
  onApplyTemplate,
  onDeleteTemplate,
  onDownloadImage,
  onCopyImage,
  onCustomIndicatorClick,
  customIndicatorCount = 0,
}: ChartToolbarProps) {
  const { panels, setTimeframe, setChartType } = useChartStore()
  const panel = panels[panelId]
  const [isTemplateOpen, setIsTemplateOpen] = useState(false)
  const [isCameraOpen, setIsCameraOpen] = useState(false)

  if (!panel) return null

  const { symbol, timeframe, chartType } = panel

  return (
    <div
      className={cn(
        'flex h-8 shrink-0 items-center gap-1 border-b border-tv-border bg-tv-bg-secondary px-2',
        isActive && 'border-b-tv-blue/30'
      )}
      onClick={onActivate}
    >
      {/* Symbol button */}
      <button
        type="button"
        onClick={onActivate}
        className="flex items-center gap-1.5 rounded px-1.5 py-0.5 hover:bg-tv-bg-tertiary"
      >
        <span className="font-mono text-xs font-semibold text-tv-text-primary">{symbol}</span>
        {liveTick && (
          <span className="font-mono text-xs text-tv-text-secondary">
            {formatPrice(liveTick.price)}
          </span>
        )}
      </button>

      <div className="h-3.5 w-px bg-tv-border" />

      {/* Timeframe pills */}
      <div className="flex items-center gap-0.5">
        {VISIBLE_TFS.map((tf) => (
          <button
            key={tf}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setTimeframe(panelId, tf)
            }}
            className={cn(
              'rounded px-1.5 py-0.5 text-2xs transition-colors hover:bg-tv-bg-tertiary',
              timeframe === tf
                ? 'bg-tv-bg-tertiary font-semibold text-tv-blue'
                : 'text-tv-text-secondary'
            )}
          >
            {TIMEFRAME_LABELS[tf]}
          </button>
        ))}
        {!VISIBLE_TFS.includes(timeframe) && (
          <span className="px-1.5 py-0.5 text-2xs font-semibold text-tv-blue">
            {TIMEFRAME_LABELS[timeframe]}
          </span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-0.5">
        {/* Indicators button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onIndicatorClick?.()
          }}
          title="Indicators"
          className={cn(
            'flex h-6 items-center gap-1 rounded px-1.5 transition-colors hover:bg-tv-bg-tertiary',
            activeIndicatorCount > 0
              ? 'text-tv-blue'
              : 'text-tv-text-muted hover:text-tv-text-secondary'
          )}
        >
          <FlaskConical className="h-3.5 w-3.5" />
          {activeIndicatorCount > 0 && (
            <span className="text-2xs font-semibold">{activeIndicatorCount}</span>
          )}
        </button>

        {/* Custom Indicators button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onCustomIndicatorClick?.()
          }}
          title="Custom Indicators"
          className={cn(
            'flex h-6 items-center gap-1 rounded px-1.5 transition-colors hover:bg-tv-bg-tertiary',
            customIndicatorCount > 0
              ? 'text-tv-blue'
              : 'text-tv-text-muted hover:text-tv-text-secondary'
          )}
        >
          <span className="font-mono text-[11px]">{'{ }'}</span>
          {customIndicatorCount > 0 && (
            <span className="text-2xs font-semibold">{customIndicatorCount}</span>
          )}
        </button>

        {/* Templates button */}
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsTemplateOpen((v) => !v)
              setIsCameraOpen(false)
            }}
            title="Chart templates"
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-tv-bg-tertiary',
              isTemplateOpen ? 'text-tv-blue' : 'text-tv-text-muted hover:text-tv-text-secondary'
            )}
          >
            <Bookmark className="h-3.5 w-3.5" />
          </button>
          {isTemplateOpen && onSaveTemplate && onApplyTemplate && onDeleteTemplate && (
            <TemplateMenu
              templates={templates}
              onSave={onSaveTemplate}
              onApply={onApplyTemplate}
              onDelete={onDeleteTemplate}
              onClose={() => setIsTemplateOpen(false)}
            />
          )}
        </div>

        {/* Camera button */}
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsCameraOpen((v) => !v)
              setIsTemplateOpen(false)
            }}
            title="Capture chart image"
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-tv-bg-tertiary',
              isCameraOpen ? 'text-tv-blue' : 'text-tv-text-muted hover:text-tv-text-secondary'
            )}
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
          {isCameraOpen && onDownloadImage && onCopyImage && (
            <CameraMenu
              onDownload={onDownloadImage}
              onCopy={onCopyImage}
              onClose={() => setIsCameraOpen(false)}
            />
          )}
        </div>

        <div className="h-3.5 w-px bg-tv-border" />

        {/* Chart type switcher */}
        {CHART_TYPES.map((ct) => (
          <button
            key={ct.type}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setChartType(panelId, ct.type)
            }}
            title={ct.label}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-tv-bg-tertiary',
              chartType === ct.type
                ? 'text-tv-blue'
                : 'text-tv-text-muted hover:text-tv-text-secondary'
            )}
          >
            {ct.icon}
          </button>
        ))}

        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-tv-blue" />}
      </div>
    </div>
  )
}
